/**
 * Thin i18n over Intl. Locale JSON files register themselves (no dynamic imports, so a missing file
 * can never break the build):
 *
 *   import common from './locales/he/common.json';
 *   registerNamespace('he', 'common', common);   // keys become "common.<path.to.key>"
 *
 * Plurals: write sibling keys `items_one`, `items_two`, `items_other` (plus _zero/_few/_many for ar)
 * and call t('owner.items', { count }). Fallback chain: active locale -> he -> the key itself.
 */
import { BUSINESS_TZ } from '../domain/types';
import type { Locale } from '../domain/types';
import { formatDate as fmtDate, formatTime as fmtTime, type DateStyle, DAY, HOUR, MINUTE, dayKeyOf, diffDays } from '../domain/time';
import { formatNis } from '../domain/money';

export type Json = string | { [key: string]: Json };
export type Params = Record<string, string | number>;

export const LOCALES: readonly Locale[] = ['he', 'en', 'ar'];
export const DEFAULT_LOCALE: Locale = 'he';
const RTL: ReadonlySet<Locale> = new Set(['he', 'ar']);
const TAGS: Record<Locale, string> = { he: 'he-IL', en: 'en-IL', ar: 'ar-IL-u-nu-latn' };

const messages = new Map<Locale, Map<string, string>>();
const listeners = new Set<() => void>();
let current: Locale = DEFAULT_LOCALE;
let missingHandler: ((key: string, locale: Locale) => void) | undefined;

function flatten(prefix: string, value: Json, out: Map<string, string>): void {
  if (typeof value === 'string') out.set(prefix, value);
  else for (const [k, v] of Object.entries(value)) flatten(prefix ? `${prefix}.${k}` : k, v, out);
}

/** Adds (or replaces) one namespace for a locale. */
export function registerNamespace(locale: Locale, namespace: string, json: Json): void {
  const table = messages.get(locale) ?? new Map<string, string>();
  for (const k of [...table.keys()]) if (k === namespace || k.startsWith(`${namespace}.`)) table.delete(k);
  flatten(namespace, json, table);
  messages.set(locale, table);
  notify();
}
/** Convenience: register several namespaces; undefined entries (missing files) are skipped. */
export function registerLocale(locale: Locale, namespaces: Record<string, Json | undefined>): void {
  for (const [ns, json] of Object.entries(namespaces)) if (json !== undefined) registerNamespace(locale, ns, json);
}
/** Resolves once the locale is active; kept async so a lazy loader can replace the registry later. */
export async function loadLocale(locale: Locale): Promise<void> {
  setLocale(locale);
}

function notify(): void { for (const cb of [...listeners]) cb(); }
/** Subscribe to locale/registry changes (useSyncExternalStore friendly with getLocale). */
export function subscribeLocale(cb: () => void): () => void { listeners.add(cb); return () => void listeners.delete(cb); }
export const getLocale = (): Locale => current;
export const isRtl = (locale: Locale = current): boolean => RTL.has(locale);
/** BCP-47 tag used for Intl formatting. */
export const localeTag = (locale: Locale = current): string => TAGS[locale];
export function setMissingHandler(fn: ((key: string, locale: Locale) => void) | undefined): void { missingHandler = fn; }

export function setLocale(locale: Locale): void {
  current = locale;
  if (typeof document !== 'undefined') {
    document.documentElement.lang = locale;
    document.documentElement.dir = RTL.has(locale) ? 'rtl' : 'ltr';
  }
  notify();
}

export function interpolate(template: string, params?: Params): string {
  return params ? template.replace(/\{(\w+)\}/g, (m, k: string) => (k in params ? String(params[k]) : m)) : template;
}

function lookup(key: string, locale: Locale, params?: Params): string | undefined {
  const table = messages.get(locale);
  if (!table) return undefined;
  if (typeof params?.count === 'number') {
    const rule = new Intl.PluralRules(TAGS[locale]).select(params.count);
    const plural = table.get(`${key}_${rule}`) ?? table.get(`${key}_other`);
    if (plural !== undefined) return plural;
  }
  return table.get(key);
}

/** Is there a translation for the key in the active locale or the Hebrew fallback? */
export function has(key: string, locale: Locale = current): boolean {
  return lookup(key, locale) !== undefined || lookup(key, DEFAULT_LOCALE) !== undefined
    || (messages.get(locale)?.has(`${key}_other`) ?? false) || (messages.get(DEFAULT_LOCALE)?.has(`${key}_other`) ?? false);
}

/** Translate a "namespace.path.key". Never throws; unknown keys return the key. */
export function t(key: string, params?: Params): string {
  return tIn(current, key, params);
}
/** Like t() for an explicit locale (messages sent in the business's language). */
export function tIn(locale: Locale, key: string, params?: Params): string {
  const text = lookup(key, locale, params) ?? lookup(key, DEFAULT_LOCALE, params);
  if (text === undefined) { missingHandler?.(key, locale); return key; }
  return interpolate(text, params);
}

// ---------------------------------------------------------------- formatting helpers

type Instant = number | string | Date;
const ms = (v: Instant): number => (typeof v === 'number' ? v : v instanceof Date ? v.getTime() : Date.parse(v));

export const formatDate = (v: Instant, style: DateStyle = 'long', tz: string = BUSINESS_TZ): string => fmtDate(ms(v), style, TAGS[current], tz);
/** 24-hour clock in the business timezone. */
export const formatTime = (v: Instant, tz: string = BUSINESS_TZ): string => fmtTime(ms(v), TAGS[current], tz);
export const formatMoney = (agorot: number): string => formatNis(agorot, TAGS[current]);
export const formatNumber = (n: number): string => new Intl.NumberFormat(TAGS[current]).format(n);

/** "בעוד 3 שעות", "מחר", "לפני 5 דקות". Day differences are calendar days in the business zone. */
export function formatRelative(v: Instant, now: Instant = Date.now(), tz: string = BUSINESS_TZ): string {
  const target = ms(v), base = ms(now), diff = target - base;
  const rtf = new Intl.RelativeTimeFormat(TAGS[current], { numeric: 'auto' });
  const abs = Math.abs(diff);
  if (abs < MINUTE) return rtf.format(0, 'second');
  if (abs < HOUR) return rtf.format(Math.round(diff / MINUTE), 'minute');
  const days = diffDays(dayKeyOf(base, tz), dayKeyOf(target, tz));
  if (abs < DAY && days === 0) return rtf.format(Math.round(diff / HOUR), 'hour');
  return rtf.format(days, 'day');
}
